import { Button, Flex, Icon } from "@chakra-ui/react";
import { t } from "i18next";
import { cloneDeep } from "lodash";
import { useCallback, useState } from "react";
import { FaCheck, FaPlus } from "react-icons/fa";
import {
  DefaultNotificationSettingsDto,
  OrganizationRoleType,
  SupplierNotificationReceiversDto,
  useUpdateOrganizationMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { MultiSelector } from "../../../common/input/Selector/MultiSelector";
import { PersonsSelector } from "../../../common/persons/PersonsSelector";
import { useAppDispatch } from "../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../common/redux/reducers/authStateReducer";
import { organizationSourcingRoles } from "../../organizations/current/manage-users/utils";
import { initSettings } from "./NotificationSettings";

type ReceiverType = "inviteReceivers" | "messageReceivers";

const initReceivers = (): SupplierNotificationReceiversDto => ({
  persons: [],
  organizationRoles: [],
});

export const SupplierNotificationReceiverForm = ({ receiverType }: { receiverType: ReceiverType }) => {
  const dispatch = useAppDispatch();
  const authState = useLoggedInWithOrgContextState();

  const [updateOrg, { isLoading }] = useUpdateOrganizationMutation();

  const [isVisible, setIsVisible] = useState(false);
  const [receivers, setReceivers] = useState<SupplierNotificationReceiversDto>(initReceivers());

  const saveReceivers = useCallback(async () => {
    const settings: DefaultNotificationSettingsDto =
      cloneDeep(authState.selectedOrg.defaultNotificationSettings) ?? initSettings();
    if (!settings.supplierNotificationSettings) {
      settings.supplierNotificationSettings = {
        inviteReceivers: initReceivers(),
        messageReceivers: initReceivers(),
      };
    }
    settings.supplierNotificationSettings[receiverType] = receivers;
    const response = await updateOrg({
      orgId: authState.selectedOrg.id,
      editOrganizationRequest: {
        defaultNotificationSettings: settings,
      },
    });

    if ("data" in response) {
      dispatch(updateSelectedOrg(response.data));
      setReceivers(initReceivers());
      setIsVisible(false);
    }
  }, [authState.selectedOrg.defaultNotificationSettings, authState.selectedOrg.id, dispatch, receivers, receiverType, updateOrg]);

  const currentReceivers =
    authState.selectedOrg.defaultNotificationSettings?.supplierNotificationSettings?.[receiverType];

  if (currentReceivers && (currentReceivers.persons.length > 0 || currentReceivers.organizationRoles.length > 0)) {
    return null;
  }

  if (!isVisible)
    return (
      <Flex>
        <Button
          rightIcon={<Icon as={FaPlus} />}
          size="xs"
          variant={"ghost"}
          colorScheme="teal"
          onClick={() => setIsVisible(true)}
        >
          {t("Configure")}
        </Button>
      </Flex>
    );

  return (
    <Flex
      position="relative"
      zIndex="2"
      flexDirection="column"
      rowGap="2"
      border="1px solid"
      borderColor="smBorder"
      rounded="md"
      p="2"
    >
      <PersonsSelector
        size="md"
        organizationIds={[authState.selectedOrg.id]}
        selectedPersons={receivers.persons}
        roles={organizationSourcingRoles}
        onChange={(persons) => {
          const updated = cloneDeep(receivers);
          updated.persons = persons;
          setReceivers(updated);
        }}
        placeholder={`${t("Select individual receivers")}...`}
      />
      <MultiSelector
        value={receivers.organizationRoles.map((role) => ({ label: role, value: role }))}
        options={organizationSourcingRoles.map((role) => ({
          label: role,
          value: role,
        }))}
        noOptionsAvailableMessage={`${t("No roles found")}...`}
        onChange={(options) => {
          const updated = cloneDeep(receivers);
          updated.organizationRoles = options.map((o) => o.value as OrganizationRoleType);
          setReceivers(updated);
        }}
        placeholder={{ text: `${t("Select receivers by role in organization")}...`, color: "" }}
      />
      <Flex>
        <Button
          colorScheme="blue"
          size={"xs"}
          rightIcon={<Icon as={FaCheck} />}
          isLoading={isLoading}
          onClick={() => saveReceivers()}
        >
          {t("Save")}
        </Button>
      </Flex>
    </Flex>
  );
};
